import {pickKeysLike} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {ButtonHTMLAttributes, PropsWithChildren} from 'react';
import {AriaButtonLinkType} from '../../../types/AriaButtonLink';

type SupportedUnstyledButtonProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'className'
  | 'onClick'
  | 'hidden'
  | 'style'
  | 'tabIndex'
  | 'role'
  | 'aria-controls'
  | 'aria-pressed'
  | 'aria-expanded'
  | 'disabled'
  | 'onKeyDown'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'type'
> & {
  [dataAttr: `data-${string}`]: string;
} & AriaButtonLinkType;

const BaseUnstyledButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<SupportedUnstyledButtonProps>
>(function Link(
  {
    className,
    onClick,
    hidden,
    disabled,
    style,
    tabIndex,
    role,
    'aria-controls': ariaControls,
    'aria-pressed': ariaPressed,
    'aria-expanded': ariaExpanded,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    children,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    ...rest
  },
  ref,
) {
  const dataAttrProps = pickKeysLike(rest, 'data-');

  return (
    <button
      className={className}
      onClick={onClick}
      hidden={hidden}
      disabled={disabled}
      style={style}
      tabIndex={tabIndex}
      role={role}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      data-testid={dataTestId}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={ref}
      {...dataAttrProps}
    >
      {children}
    </button>
  );
});

export const UnstyledButton = styled(BaseUnstyledButton)`
  appearance: none;
  font: inherit;
  color: inherit;
  padding: 0;
  border: unset;
  background: transparent;
  cursor: pointer;
`;
